import React, { type FC, useMemo } from "react";
import { BusinessForm } from "@src/components/business-form";
import { BusinessLotValue, BusinessMeasureValue, BusinessStockObjValue } from "@src/components/business-data-display";
import { DetailList } from "@shared/ui/detail-row";
import { EmptyState } from "@shared/ui/empty-state";
import FormFooter from "@shared/ui/form-footer";
import { FormPanel } from "@shared/ui/form-panel";
import { Input } from "@shared/ui/input";
import { measureEndDecorator } from "@src/helpers/business/measureEndDecorator";
import type { StockGatheringReserveItem } from "../../../../../../api/stockGatherings";
import { formatOrderText } from "../../../../../StockFinishedGoodsReceipt/modules/Card/helpers/formatFinishedGoodsReceiptContent";
import { formatReserveCells } from "../StockGatheringCompletionReservesList/StockGatheringCompletionReserveListItem";
import type { UseStockGatheringCompletionCardMutationsResult } from "../../useStockGatheringCompletionCardMutations";
import {
    stockGatheringCompletionReserveFormConfig,
    type StockGatheringCompletionReserveFormContext,
    type StockGatheringCompletionReserveFormValues,
} from "./stockGatheringCompletionReserve.form-config";

/** Пропсы компонента {@link StockGatheringCompletionReserveForm}. */
export interface StockGatheringCompletionReserveFormProps {
    /** Код КВ. */
    gatheringCode: number;
    /** Код резерва. */
    reserveCode: number;
    /** Резервы текущей КВ. */
    reserves: StockGatheringReserveItem[];
    /** Мутация отпуска резерва. */
    completeReserveMutation: UseStockGatheringCompletionCardMutationsResult["completeReserveMutation"];
}

/** Форма комплектования резерва по строке состава КВ. */
export const StockGatheringCompletionReserveForm: FC<StockGatheringCompletionReserveFormProps> = ({
    gatheringCode,
    reserveCode,
    reserves,
    completeReserveMutation,
}) => {
    const reserve = reserves.find((item) => item.Code === reserveCode);

    if (reserve == null) {
        return <EmptyState text="Резерв КВ не найден" />;
    }

    return (
        <StockGatheringCompletionReserveFormBody
            gatheringCode={gatheringCode}
            reserve={reserve}
            completeReserveMutation={completeReserveMutation}
        />
    );
};

interface StockGatheringCompletionReserveFormBodyProps {
    gatheringCode: number;
    reserve: StockGatheringReserveItem;
    completeReserveMutation: UseStockGatheringCompletionCardMutationsResult["completeReserveMutation"];
}

const StockGatheringCompletionReserveFormBody: FC<StockGatheringCompletionReserveFormBodyProps> = ({
    gatheringCode,
    reserve,
    completeReserveMutation,
}) => {
    const formContext = useMemo(
        (): StockGatheringCompletionReserveFormContext => ({
            gatheringCode,
            gatheringContCode: reserve.GatheringContCode,
            reserveCode: reserve.Code,
            lotCode: reserve.LotCode,
        }),
        [gatheringCode, reserve.Code, reserve.GatheringContCode, reserve.LotCode],
    );
    const boundFormConfig = useMemo(() => stockGatheringCompletionReserveFormConfig.withContext(formContext), [formContext]);
    const initialValues = useMemo(
        (): StockGatheringCompletionReserveFormValues => ({
            qty: reserve.Qty,
        }),
        [reserve.Qty],
    );

    const handleSubmit = async (values: StockGatheringCompletionReserveFormValues): Promise<void> => {
        await completeReserveMutation.mutateAsync({
            gatheringCode,
            gatheringContCode: reserve.GatheringContCode,
            reserveCode: reserve.Code,
            lotCode: reserve.LotCode,
            qty: values.qty,
        });
    };

    return (
        <FormPanel<StockGatheringCompletionReserveFormValues>
            useDefaultFooter={false}
            formProps={{
                formConfig: boundFormConfig,
                initialValues,
                ready: true,
                onSubmit: handleSubmit,
            }}
            footer={({ submitForm, isFormSubmitting }) => (
                <FormFooter
                    confirmText="Скомплектовать"
                    confirmLoading={isFormSubmitting}
                    confirmDisabled={reserve.Qty <= 0}
                    onConfirm={submitForm}
                />
            )}
        >
            {(form) => (
                <>
                    <DetailList layout="tiles" columns="auto" minWidth={96} oneLine>
                        <DetailList.Row label="Партия" value={<BusinessLotValue lot={reserve.Lot} />} />
                        <DetailList.Row
                            label="Номенклатура"
                            value={<BusinessStockObjValue stockObj={reserve.Content?.StockObj} />}
                        />
                        <DetailList.Row label="Заказ" value={formatOrderText(reserve.Order)} />
                        <DetailList.Row
                            label="Кол-во резерва"
                            value={<BusinessMeasureValue value={reserve.Qty} measure={reserve.Measure} />}
                        />
                        <DetailList.Row label="Ячейка" value={formatReserveCells(reserve.StorePlaceRemainders)} />
                    </DetailList>
                    {reserve.Qty <= 0 && <EmptyState text="Резерв уже исчерпан" />}
                    <BusinessForm.Field name="qty">
                        <Input
                            type="number"
                            {...form.field("qty").getNumberInputProps()}
                            endDecorator={measureEndDecorator(reserve.Measure)}
                        />
                    </BusinessForm.Field>
                </>
            )}
        </FormPanel>
    );
};
